import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faSearch } from "@fortawesome/pro-regular-svg-icons/faSearch";
import { faBooks } from "@fortawesome/pro-solid-svg-icons/faBooks";
import { faCog } from "@fortawesome/pro-solid-svg-icons/faCog";
import { faEnvelope } from "@fortawesome/pro-solid-svg-icons/faEnvelope";
import { faPlus } from "@fortawesome/pro-solid-svg-icons/faPlus";
import AntDMenu, { MenuProps } from "antd/es/menu";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityType, PermissionName } from "@mapmycustomers/shared";
import PermissionType from "@mapmycustomers/shared/enum/PermissionType";
import { isMacOS } from "@mapmycustomers/shared/util/browser";
import hasPermissionForEntityType from "@mapmycustomers/shared/util/hasPermissionForEntityType";
import { useNonNullableContext } from "@mapmycustomers/shared/util/react";
import { useModal } from "@mapmycustomers/ui";

import { showGlobalCreateActivityModal } from "@app/component/createEditEntity/Activity/store/actions";
import { showGlobalCreateCompanyModal } from "@app/component/createEditEntity/CreateCompanyModal/store/actions";
import { showGlobalCreateDealModal } from "@app/component/createEditEntity/CreateDealModal/store/actions";
import { showGlobalCreatePersonModal } from "@app/component/createEditEntity/CreatePersonModal/store/actions";
import EmailComposeModalsContext from "@app/component/email/creation/EmailComposeModalsContext";
import EmailTemplatesModalContext from "@app/component/email/EmailTemplatesModalContext";
import Notifications from "@app/component/NavigationBar/component/Notifications";
import Path from "@app/enum/Path";
import SettingPath from "@app/enum/settings/SettingPath";
import { checkEmailSupporting } from "@app/store/email/actions";
import { showGlobalSearch } from "@app/store/globalSearch/actions";
import {
  doesUserHaveAccessToImports,
  hasPermission,
  isEmailServiceSupported,
} from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import navbarAnalytics, { navbarRightMenuClick } from "@app/util/analytic/navbarAnalytics";
import useDefaultSourceObjectMessageArgs from "@app/util/hook/useDefaultSourceObjectMessageArgs";
import { useRecordLimitCheck } from "@app/util/hook/useRecordLimitCheck";

import menuStyles from "./CommonMenu.module.scss";
import MenuItemTitle from "./MenuItemTitle";
import styles from "./RightMenu.module.scss";

const RightMenu = ({
  canCreateImports,
  doesUserHaveAccessToImports,
  hasPermission,
  isEmailServiceSupported,
  onCheckEmailSupporting,
  onShowCreateActivityModal,
  onShowCreateCompanyModal,
  onShowCreateDealModal,
  onShowCreatePersonModal,
  onShowGlobalSearch,
}: ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();
  const defaultCompanyArgs = useDefaultSourceObjectMessageArgs(EntityType.COMPANY);
  const defaultPersonArgs = useDefaultSourceObjectMessageArgs(EntityType.PERSON);

  const [modal, modalContextHolder] = useModal();

  const { onShowEmailTemplatesModal } = useNonNullableContext(EmailTemplatesModalContext);
  const { onEmailModalShow } = useNonNullableContext(EmailComposeModalsContext);

  const handleNavigate = useCallback<NonNullable<MenuProps["onSelect"]>>((info) => {
    navbarRightMenuClick(info!.key);
  }, []);

  const checkRecordLimitBeforeProceeding = useRecordLimitCheck();

  const showCreateCompanyModal = useCallback(() => {
    checkRecordLimitBeforeProceeding(() =>
      onShowCreateCompanyModal({ showPostCreatePrompt: true })
    );
  }, [onShowCreateCompanyModal, checkRecordLimitBeforeProceeding]);

  const showCreatePersonModal = useCallback(() => {
    checkRecordLimitBeforeProceeding(() => onShowCreatePersonModal({ showPostCreatePrompt: true }));
  }, [onShowCreatePersonModal, checkRecordLimitBeforeProceeding]);

  const showCreateDealModal = useCallback(() => {
    checkRecordLimitBeforeProceeding(() => onShowCreateDealModal({ showPostCreatePrompt: true }));
  }, [onShowCreateDealModal, checkRecordLimitBeforeProceeding]);

  const handleSearchClick = useCallback(() => {
    onShowGlobalSearch();
  }, [onShowGlobalSearch]);

  const handleEmailTemplatesClick = useCallback(() => {
    navbarAnalytics.clicked(["Email Templates"]);
    onShowEmailTemplatesModal();
  }, [onShowEmailTemplatesModal]);

  const handleEmailOutboxClick = useCallback(() => {
    navbarAnalytics.clicked(["Email Outbox"]);
  }, []);

  const handleEmailClick = useCallback(() => {
    navbarAnalytics.clicked(["Email"]);
    onCheckEmailSupporting({
      modal,
      successfulCallback: () => {
        onEmailModalShow();
      },
    });
  }, [modal, onEmailModalShow, onCheckEmailSupporting]);

  const handleOnboardingClick = useCallback(() => {
    navbarAnalytics.clicked(["Onboarding"]);
  }, []);

  const handleSettingsClick = useCallback(() => {
    navbarAnalytics.clicked(["Settings"]);
  }, []);

  const canCreateActivities = hasPermissionForEntityType(
    hasPermission,
    PermissionType.CREATE,
    EntityType.ACTIVITY
  );

  const canCreatePeople = hasPermissionForEntityType(
    hasPermission,
    PermissionType.CREATE,
    EntityType.PERSON
  );

  const canCreateCompanies = hasPermissionForEntityType(
    hasPermission,
    PermissionType.CREATE,
    EntityType.COMPANY
  );

  const canCreateDeals = hasPermissionForEntityType(
    hasPermission,
    PermissionType.CREATE,
    EntityType.DEAL
  );

  const showUniversalCreateButton =
    canCreateCompanies ||
    canCreatePeople ||
    canCreateDeals ||
    canCreateActivities ||
    canCreateImports;

  return (
    <>
      <AntDMenu
        className={cn(menuStyles.menu, styles.menu, {
          [styles.createButtonVisible]: showUniversalCreateButton,
          [styles.emailVisible]: isEmailServiceSupported,
        })}
        mode="horizontal"
        onSelect={handleNavigate}
        triggerSubMenuAction="click"
      >
        <AntDMenu.Item className={menuStyles.topItem} key="search">
          <MenuItemTitle
            data-action-id="global-search"
            icon={faSearch}
            itemName="global-search"
            onClick={handleSearchClick}
            tooltip={
              <div>
                <div>
                  {intl.formatMessage({
                    id: "navbar.findIcon.tooltip",
                    defaultMessage: "Search anything",
                    description: "Title of navbar search icon",
                  })}
                </div>
                <div>{isMacOS() ? "⇧⌘F" : "Ctrl+Shift+F"}</div>
              </div>
            }
          />
        </AntDMenu.Item>
        {showUniversalCreateButton && (
          <AntDMenu.SubMenu
            className={menuStyles.topItem}
            key="add"
            popupClassName={menuStyles.submenu}
            popupOffset={[16, 0]}
            title={
              <MenuItemTitle
                data-action-id="global-add-menu"
                icon={faPlus}
                itemName="global-add-menu"
              />
            }
          >
            {canCreateCompanies && (
              <AntDMenu.Item key="addCompany" onClick={showCreateCompanyModal}>
                {intl.formatMessage(
                  {
                    id: "navbar.rightMenu.addCompany",
                    defaultMessage:
                      "Add {sourceObject, select, standard {Company} leadCompanies {Lead} siteCompanies {Site} projectCompanies {Project} shopCompanies {Shop} other {{label}}}",
                    description:
                      'Title of the Add Company plus item. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
                  },
                  defaultCompanyArgs
                )}
              </AntDMenu.Item>
            )}
            {canCreateCompanies && (
              <AntDMenu.Item key="addPerson" onClick={showCreatePersonModal}>
                {intl.formatMessage(
                  {
                    id: "navbar.rightMenu.addPerson",
                    defaultMessage:
                      "Add {sourceObject, select, standard {Person} partnerPeople {Partner} leadPeople {Lead} staffMemberPeople {Staff Member} leadPersonnel {Lead Personnel} other {{label}}}",
                    description:
                      'Title of the Add Person plus item. The record type name comes from the select: the web\'s own word for a known type, otherwise {label}, a name an admin typed or an integration sent. Keep {label} in its base form. In es, pt and fr, no article, adjective or participle may agree with it: put gendered words on the noun "record" with the name in parentheses, or put a verb before the name. In a choice that creates a record, use a form like "Добавить: {label}".',
                  },
                  defaultPersonArgs
                )}
              </AntDMenu.Item>
            )}
            {canCreateDeals && (
              <AntDMenu.Item key="addDeal" onClick={showCreateDealModal}>
                {intl.formatMessage({
                  id: "navbar.rightMenu.addDeal",
                  defaultMessage: "Add Deal",
                  description: "Title of the Add Deal plus item",
                })}
              </AntDMenu.Item>
            )}
            {canCreateActivities && (
              <AntDMenu.Item key="addActivity" onClick={() => onShowCreateActivityModal(undefined)}>
                {intl.formatMessage({
                  id: "navbar.rightMenu.addActivity",
                  defaultMessage: "Add Activity",
                  description: "Title of the Add Activity plus item",
                })}
              </AntDMenu.Item>
            )}
            {doesUserHaveAccessToImports && <AntDMenu.Divider />}
            {doesUserHaveAccessToImports && canCreateImports && (
              <AntDMenu.Item key="import">
                <MenuItemTitle
                  itemName="import"
                  path={`${Path.SETTINGS}/${SettingPath.DATA_NEW_IMPORT}`}
                >
                  {intl.formatMessage({
                    id: "navbar.rightMenu.import",
                    defaultMessage: "Import",
                    description: "Title of the Import plus item",
                  })}
                </MenuItemTitle>
              </AntDMenu.Item>
            )}
          </AntDMenu.SubMenu>
        )}
        <AntDMenu.Item className={menuStyles.topItem} key="notifications">
          <Notifications />
        </AntDMenu.Item>
        {isEmailServiceSupported && (
          <AntDMenu.SubMenu
            className={menuStyles.topItem}
            key="emailMenu"
            popupClassName={menuStyles.submenu}
            popupOffset={[16, 0]}
            title={
              <MenuItemTitle
                data-action-id="global-email"
                icon={faEnvelope}
                itemName="global-email"
              />
            }
          >
            <AntDMenu.Item key="email" onClick={handleEmailClick}>
              {intl.formatMessage({
                id: "navbar.rightMenu.composeEmail",
                defaultMessage: "Compose Email",
                description: "Title of the Compose email item",
              })}
            </AntDMenu.Item>
            <AntDMenu.Item key="emailTemplates" onClick={handleEmailTemplatesClick}>
              {intl.formatMessage({
                id: "navbar.rightMenu.emailTemplates",
                defaultMessage: "Email Templates",
                description: "Title of the Email templates item",
              })}
            </AntDMenu.Item>
            <AntDMenu.Item key="emailOutbox" onClick={handleEmailOutboxClick}>
              <MenuItemTitle itemName="email-outbox" path={Path.EMAIL_OUTBOX}>
                {intl.formatMessage({
                  id: "navbar.rightMenu.emailOutbox",
                  defaultMessage: "Email Outbox",
                  description: "Title of the Email Outbox item",
                })}
              </MenuItemTitle>
            </AntDMenu.Item>
          </AntDMenu.SubMenu>
        )}
        <AntDMenu.Item
          className={menuStyles.topItem}
          key="onboarding"
          onClick={handleOnboardingClick}
        >
          <MenuItemTitle
            data-action-id="onboarding-page"
            icon={faBooks}
            itemName={Path.ONBOARDING}
            path={Path.ONBOARDING}
          />
        </AntDMenu.Item>
        <AntDMenu.Item className={menuStyles.topItem} key="settings" onClick={handleSettingsClick}>
          <MenuItemTitle
            data-action-id="settings-page"
            icon={faCog}
            itemName={Path.SETTINGS}
            path={Path.SETTINGS}
          />
        </AntDMenu.Item>
      </AntDMenu>
      {modalContextHolder}
    </>
  );
};

export const mapStateToProps = (state: RootState) => ({
  canCreateImports: hasPermission(state)(PermissionName.IMPORT_CREATE),
  doesUserHaveAccessToImports: doesUserHaveAccessToImports(state),
  hasPermission: hasPermission(state),
  isEmailServiceSupported: isEmailServiceSupported(state),
});

const mapDispatchToProps = {
  onCheckEmailSupporting: checkEmailSupporting,
  onShowCreateActivityModal: showGlobalCreateActivityModal,
  onShowCreateCompanyModal: showGlobalCreateCompanyModal,
  onShowCreateDealModal: showGlobalCreateDealModal,
  onShowCreatePersonModal: showGlobalCreatePersonModal,
  onShowGlobalSearch: showGlobalSearch,
};

export default connect(mapStateToProps, mapDispatchToProps)(RightMenu);
